'use client';

import type { HouseholdOnboardingStatusRibbonRow } from 'src/features/advisor-book-onboarding/components/household-onboarding-status-ribbon-gql';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Button } from '@savvy-kit/components/button';
import { Text } from '@savvy-kit/foundations/text';
import { Ribbon } from '@savvy-kit/patterns/ribbon';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { FixContactInfoDialog } from 'src/features/advisor-book-onboarding/components/cockpit/fix-contact-info-dialog';
import { OnboardingCockpitStatusBadge } from 'src/features/advisor-book-onboarding/components/cockpit/onboarding-cockpit-status-badge';
import { HouseholdOnboardingDetailSheet } from 'src/features/advisor-book-onboarding/components/household-onboarding-detail-sheet';
import {
  householdOnboardingPrimaryCtaLabel,
  householdOnboardingPrimaryHrefFor,
} from 'src/features/advisor-book-onboarding/components/household-onboarding-primary-cta';
import {
  householdOnboardingPrimaryCtaFor,
  householdOnboardingRibbonVariantFor,
  householdOnboardingSummaryFor,
} from 'src/features/advisor-book-onboarding/constants/household-onboarding-status-copy';
import {
  ADVISOR_BOOK_ONBOARDING_ROUTES,
  HOUSEHOLD_ONBOARDING_DETAIL_QUERY_KEY,
  HOUSEHOLD_ONBOARDING_DETAIL_QUERY_VALUE,
} from 'src/features/advisor-book-onboarding/constants/routes';
import { useMarkOnboardingHouseholdComplete } from 'src/features/advisor-book-onboarding/hooks/use-mark-onboarding-household-complete';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

export type { HouseholdOnboardingStatusRibbonRow };

const DismissOnboardingHouseholdFromRibbon = graphql(/* GraphQL */ `
  mutation DismissOnboardingHouseholdFromRibbon($input: DismissOnboardingHouseholdInput!) {
    dismissOnboardingHousehold(input: $input) {
      success
    }
  }
`);

type HouseholdOnboardingStatusRibbonProps = {
  readonly row: HouseholdOnboardingStatusRibbonRow;
  readonly onDismissed?: () => void;
  readonly onContactInfoResolved?: () => void;
  readonly onMarkedComplete?: () => void;
  readonly onInviteResent?: () => void;
};

export function HouseholdOnboardingStatusRibbon({
  row,
  onDismissed,
  onContactInfoResolved,
  onMarkedComplete,
  onInviteResent,
}: HouseholdOnboardingStatusRibbonProps) {
  const router = useRouter();
  const auth = useAuth();
  const isAdminUser = auth.user?.role === 'ADMIN' || auth.user?.role === 'ADMIN_CSA';
  const [fixContactOpen, setFixContactOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dismissHousehold, { loading: dismissing }] = useMutationWithErrorLogging(DismissOnboardingHouseholdFromRibbon);
  const { confirmMarkComplete } = useMarkOnboardingHouseholdComplete({
    onSuccess: () => {
      setDetailOpen(false);
      onMarkedComplete?.();
    },
  });
  const onboardingDetailParam = router.query[HOUSEHOLD_ONBOARDING_DETAIL_QUERY_KEY];

  useEffect(() => {
    if (onboardingDetailParam !== HOUSEHOLD_ONBOARDING_DETAIL_QUERY_VALUE) {
      return;
    }
    setDetailOpen(true);
  }, [onboardingDetailParam]);

  const summary = householdOnboardingSummaryFor({
    status: row.status,
    blockedReason: row.blockedReason,
    accountsTransferred: row.accountsTransferred,
    accountsTotal: row.accountsTotal,
  });
  const primaryCta = householdOnboardingPrimaryCtaFor({
    status: row.status,
    blockedReason: row.blockedReason,
  });
  const ribbonVariant = householdOnboardingRibbonVariantFor(row.status);
  const primaryHref = householdOnboardingPrimaryHrefFor({
    kind: primaryCta?.kind,
    householdPublicId: row.householdUuid ?? null,
  });

  async function handleDismiss() {
    try {
      const result = await dismissHousehold({
        variables: { input: { stagedHouseholdId: row.id } },
      });
      const payload = result.data?.dismissOnboardingHousehold;
      if (isNil(payload) || !payload.success) {
        toast.error('Failed to dismiss household');
        return;
      }
      toast.success('Household dismissed');
      setDetailOpen(false);
      onDismissed?.();
    } catch (error: unknown) {
      handleMutationError(error, 'Failed to dismiss household');
    }
  }

  function handlePrimaryCta() {
    if (isNil(primaryCta)) {
      return;
    }
    if (primaryCta.kind === 'fix_contact_info') {
      setFixContactOpen(true);
      return;
    }
    if (primaryCta.kind === 'dismiss') {
      void handleDismiss();
    }
  }

  return (
    <>
      <Ribbon
        variant={ribbonVariant}
        className="w-full rounded-none px-8"
        description={
          <div className="flex min-w-0 items-center gap-3">
            <OnboardingCockpitStatusBadge
              status={row.status}
              blockedReason={row.blockedReason}
              accountsTransferred={row.accountsTransferred}
              accountsTotal={row.accountsTotal}
              primaryClientName={row.primaryClientName}
              lastContactedAt={row.lastContactedAt}
            />
            <Text variant="body-sm" color="dark" className="min-w-0">
              {summary}
            </Text>
          </div>
        }
        actionButton={
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDetailOpen(true)}>
              View detail
            </Button>
            {!isNil(primaryCta) && primaryHref !== null && (
              <Button variant="default" size="sm" nativeButton={false} render={<Link href={primaryHref} />}>
                {householdOnboardingPrimaryCtaLabel(primaryCta.kind)}
              </Button>
            )}
            {!isNil(primaryCta) &&
              primaryHref === null &&
              (primaryCta.kind === 'fix_contact_info' || primaryCta.kind === 'dismiss') && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handlePrimaryCta}
                  disabled={primaryCta.kind === 'dismiss' && dismissing}
                >
                  {householdOnboardingPrimaryCtaLabel(primaryCta.kind)}
                </Button>
              )}
          </div>
        }
      />
      <HouseholdOnboardingDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        row={row}
        dismissing={dismissing}
        onFixContactInfo={() => setFixContactOpen(true)}
        onDismiss={() => {
          void handleDismiss();
        }}
        onMarkComplete={isAdminUser ? () => confirmMarkComplete(row) : undefined}
        onInviteResent={onInviteResent}
      />
      {primaryCta?.kind === 'fix_contact_info' && (
        <FixContactInfoDialog
          open={fixContactOpen}
          stagedHouseholdUuid={row.uuid}
          householdName={row.householdName}
          onOpenChange={setFixContactOpen}
          onResolved={() => {
            setFixContactOpen(false);
            onContactInfoResolved?.();
          }}
          onOpenBulkUpload={() => {
            setFixContactOpen(false);
            void router.push(
              ADVISOR_BOOK_ONBOARDING_ROUTES.bookTransferCockpit({
                openBulkUpload: true,
                query: row.householdName,
              }),
            );
          }}
        />
      )}
    </>
  );
}
